import { Environment, PerformanceMonitor, useGLTF } from "@react-three/drei";
import { LevaWrapper } from "@core";
import { Canvas, useLoader } from "@react-three/fiber";
import { useEffect, Suspense, useMemo, useState } from "react";
import { DirectionalLight } from "../components/DirectionalLight";
import { WebGPURenderer } from "three/webgpu";
import Effects from "../components/Effects/Effects";
import { useGameStore } from "../core/store/gameStore";
import { CameraViewControl } from "../components/camera/CameraViewControl";
import { AudioManager } from "@core";
import { DeviceDetector } from "../core/utils/DeviceDetector";
import { UI } from "../ui/UI";
import { preloadVATAssets } from "../components/Rose/core";
import { WorldController } from "../components/WorldController";
import { createContext } from "react";
import * as THREE from "three/webgpu";
import { KeyboardMapper } from "@core";
import { input, keyBindings } from "../core/input/controls";
import { useShortcut } from "@core/hooks/useShortcut";
import { AudioLoader } from 'three';
import { KTX2Preloader } from "@core";
import { ROSE_TEXTURES } from "../components/Rose/core/config";
import { BODY_TEXTURE_PATHS, DETAIL_TEXTURE_PATHS, MODEL_PATHS } from '../components/character/config';
import { publicUrl } from "../utils/publicUrl";


useLoader.preload(AudioLoader,
    [publicUrl("/audio/fs_grass1.mp3"),
        publicUrl("/audio/fs_grass2.mp3"),
        publicUrl("/audio/fs_grass3.mp3"),
        publicUrl("/audio/fs_grass4.mp3"),
        publicUrl("/audio/fs_grass5.mp3")]);

useLoader.preload(AudioLoader, [publicUrl("/audio/wave01.mp3")]);

useGLTF.preload(MODEL_PATHS);

preloadVATAssets(publicUrl("/vat/Rose_meta.json"));
preloadVATAssets(publicUrl("/vat/RoseLowPoly_meta.json"));

export const BeamSceneContext = createContext<THREE.Scene | null>(null);

export default function App() {
    const beamScene = useMemo(() => new THREE.Scene(), []);
    const [dpr, setDpr] = useState(1.5);
    /** 异步检测完成前禁止挂载 Canvas，避免 WebGPURenderer 在 navigator/gpu API 尚未就绪时报 GPUShaderStage.VERTEX undefined */
    const [webGpuReady, setWebGpuReady] = useState(false);

    const toggleCameraMode = useGameStore((state) => state.toggleCameraMode);
    const setGpuError = useGameStore((state) => state.setGpuError);
    const setAudioListener = useGameStore((state) => state.setAudioListener);
    const gpuError = useGameStore((state) => state.gpuError);

    // Check WebGPU support on mount — 通过后再挂载 R3F Canvas
    useEffect(() => {
        let cancelled = false;
        const checkWebGPU = async () => {
            setWebGpuReady(false);

            if (!navigator.gpu) {
                if (!cancelled) setGpuError("WEBGPU NOT SUPPORTED");
                console.error("WebGPU is not supported in this browser");
                return;
            }

            // 部分环境下 navigator.gpu 存在但 WebGPU 常量未挂载，Three 仍会读 GPUShaderStage.VERTEX 并崩溃
            if (
                typeof globalThis.GPUShaderStage === "undefined" ||
                globalThis.GPUShaderStage?.VERTEX === undefined
            ) {
                if (!cancelled) setGpuError("WEBGPU API INCOMPLETE");
                console.error("GPUShaderStage is unavailable — use HTTPS, WebGPU-supported browser");
                return;
            }

            try {
                const adapter = await navigator.gpu.requestAdapter();
                if (!adapter) {
                    if (!cancelled) setGpuError("NO GPU ADAPTER FOUND");
                    console.error("No GPU adapter found");
                    return;
                }

                console.log('WebGPU initialized successfully');
                if (!cancelled) {
                    setGpuError(null);
                    setWebGpuReady(true);
                }
            } catch (e) {
                if (!cancelled) setGpuError("GPU INIT FAILED");
                console.error("WebGPU initialization failed:", e);
            }
        };
        checkWebGPU();
        return () => {
            cancelled = true;
        };
    }, [setGpuError]);

    useShortcut('c', () => {
        toggleCameraMode();
    });

    return <>
        <LevaWrapper collapsed={true} initialHidden={true} />
        <DeviceDetector />
        <UI />
        <KeyboardMapper input={input} keyMap={keyBindings} />


        {webGpuReady && !gpuError && (
            <Canvas
                camera={{
                    fov: 45,
                    near: 0.1,
                    far: 200,
                    position: [20, 20, 30]
                }}
                gl={(canvas) => {
                    const renderer = new WebGPURenderer({
                        ...canvas as any,
                        powerPreference: "high-performance",
                        antialias: true,
                        alpha: true,
                    });
                    renderer.setClearColor('#000000');
                    renderer.autoClear = true;
                    // renderer.inspector = new Inspector();
                    renderer.sortObjects = false;

                    return renderer.init().then(() => renderer);
                }}
                dpr={dpr}
            >
                <Suspense fallback={null}>
                    <KTX2Preloader paths={ROSE_TEXTURES} />
                    <KTX2Preloader paths={BODY_TEXTURE_PATHS} />
                    <KTX2Preloader paths={DETAIL_TEXTURE_PATHS} />
                </Suspense>

                <AudioManager onListenerCreated={setAudioListener} />

                <PerformanceMonitor
                    bounds={() => [28, 32]}
                    onFallback={() => setDpr(1)}
                    onChange={({ factor }) => {
                        const targetDpr = 1 + 1 * factor;
                        setDpr(targetDpr);
                        // console.log("factor", factor, "target DPR", targetDpr);
                    }}
                />

                <BeamSceneContext.Provider value={beamScene}>
                    <WorldController />

                    <Suspense fallback={null}>
                        <color attach="background" args={['#000000']} />
                        <CameraViewControl />
                        <Environment
                            files={publicUrl("/textures/potsdamer_platz_1k_nb.hdr")}
                            environmentIntensity={0.5}
                        />
                        <DirectionalLight />
                        <Effects />
                    </Suspense>
                </BeamSceneContext.Provider>
            </Canvas>
        )}
    </>
}
